/**
 * m3 echo Frontend - Create Autopilot Dialog
 *
 * Dialog for creating autopilots (LLM Score, using the project's existing
 * prompts; the models come from the central lineup).
 *
 * The same configuration costs two different amounts: an autopilot switched on
 * mid-month only books the runs that are actually left, so the opening month is
 * prorated, while from the next month it costs the flat full-month price. Both
 * numbers are shown before saving - discovering the second one from a rejection
 * a month later is the failure mode this dialog exists to prevent.
 *
 * The figures are never computed here. `previewBooking` runs the server's own
 * booking arithmetic, so the bar and the booking cannot disagree.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  createAutopilot,
  previewBooking,
  AutopilotQuota,
  BookingPreview,
  CreateAutopilotRequest,
  PreviewBookingRequest,
  ScheduleConfig,
  ScheduleType,
} from '../services/autopilots';
import { describeApiError, projectsApi } from '../services/api';
import MonthlyQuotaPanel from './quota/MonthlyQuotaPanel';
import { QUOTA_OVER_LIMIT_INK } from './quota/QuotaBar';
import QuotaRefusal, { refusalReasons } from './quota/QuotaRefusal';
import { bookingPreviewKey, invalidateQuotaViews } from './quota/quotaQueries';

/** 29-31 do not exist in every month; the server rejects anything above this. */
const MAX_DAY_OF_MONTH = 28;
const PREVIEW_DEBOUNCE_MS = 300;

/**
 * Choosing a cadence is the one place the weekday belongs in the label - it is
 * information the customer needs to decide, and there is nowhere else on this
 * screen to put it. Once an autopilot exists, `AutopilotManagementCard` names
 * the day in its schedule line instead, and its booking ledger drops it
 * entirely; a ledger row is about what was spent, not when it runs.
 */
const CADENCES: ReadonlyArray<{ value: ScheduleType; label: string; runs: string }> = [
  { value: 'daily', label: 'Täglich', runs: '30 Läufe pro Monat' },
  { value: 'weekly', label: 'Wöchentlich (montags)', runs: '4 Läufe pro Monat' },
  { value: 'monthly', label: 'Monatlich', runs: '1 Lauf pro Monat' },
];

/** Today, capped so a monthly autopilot still has a run date in February. */
const defaultDayOfMonth = () => Math.min(new Date().getDate(), MAX_DAY_OF_MONTH);

/**
 * The cadence half of a request. Returns null for a monthly cadence without a
 * usable day: the server 422s that, and the union type refuses to build it.
 */
const buildScheduleConfig = (
  scheduleType: ScheduleType,
  dayOfMonth: number | null
): ScheduleConfig | null => {
  if (scheduleType === 'monthly') {
    return dayOfMonth === null
      ? null
      : { schedule_type: 'monthly', schedule_day_of_month: dayOfMonth };
  }
  return { schedule_type: scheduleType };
};

/** What the preview was last asked about - the debounced snapshot of the form. */
interface PreviewConfig {
  promptCount: number;
  scheduleType: ScheduleType;
  /** Only ever set for the monthly cadence. */
  day: number | null;
}

const buildPreviewRequest = (config: PreviewConfig | null): PreviewBookingRequest | null => {
  if (!config || config.promptCount <= 0) return null;
  const schedule = buildScheduleConfig(config.scheduleType, config.day);
  if (!schedule) return null;
  return { prompt_count: config.promptCount, ...schedule };
};

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: number;
  projectName: string;
  /**
   * The quota the booking will actually be charged against - one object rather
   * than an object plus a copy of one of its fields. `undefined` means *not
   * known yet* (or the read failed), which is not the same statement as
   * `available === 0`; folding both into a number made an unloaded quota render
   * as an exhausted one.
   */
  quota?: AutopilotQuota;
  /**
   * The quota read failed. `quota === undefined` alone cannot say whether the
   * bars are still coming or never will, and rendering "wird geladen" for a
   * request that has already given up leaves a spinner-shaped lie on screen
   * for as long as the dialog is open.
   */
  quotaError?: boolean;
  /** Set when an admin is working inside another tenant's project. */
  tenantId?: number;
}

const CreateAutopilotDialog: React.FC<Props> = ({
  open,
  onClose,
  projectId,
  projectName,
  quota,
  quotaError = false,
  tenantId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('daily');
  const [dayInput, setDayInput] = useState<string>(() => String(defaultDayOfMonth()));
  const [debounced, setDebounced] = useState<PreviewConfig | null>(null);
  /** The re-check that runs on save, before the create is sent. */
  const [revalidating, setRevalidating] = useState(false);

  // Fetch project to get the latest job's prompts (models come from the central lineup)
  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.getById(projectId).then(res => res.data),
    enabled: open,
  });

  /**
   * The prompts the autopilot would run, taken from the project's most recent
   * job. Derived once here rather than at submit time, because the count is
   * what the preview prices.
   */
  const promptSelection = useMemo(() => {
    if (!project || !project.jobs || project.jobs.length === 0) return [];
    const sortedJobs = [...project.jobs].sort(
      (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return (
      sortedJobs[0].job_prompts?.map((jp: any) => ({
        prompt_id: jp.prompt_id,
        prompt_values: jp.prompt_values || {},
      })) || []
    );
  }, [project]);
  const promptCount = promptSelection.length;

  // `Number`, not `parseInt`: parseInt("5.9") is 5, which would leave the field
  // showing 5.9 while the request said 5 - a field and a price that disagree.
  const parsedDay = dayInput.trim() === '' ? Number.NaN : Number(dayInput);
  const dayValid =
    Number.isInteger(parsedDay) && parsedDay >= 1 && parsedDay <= MAX_DAY_OF_MONTH;
  const currentDay = scheduleType === 'monthly' ? (dayValid ? parsedDay : null) : null;

  // Re-opening starts from the defaults rather than from whatever the previous
  // visit left behind - including a stale error for an autopilot that exists.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setNotice(null);
    setRevalidating(false);
    setScheduleType('daily');
    setDayInput(String(defaultDayOfMonth()));
  }, [open]);

  // Debounce: the customer flips cadences and edits the day faster than the
  // round trip, and every keystroke is not worth a request.
  useEffect(() => {
    if (!open) {
      setDebounced(null);
      return;
    }
    const handle = window.setTimeout(
      () => setDebounced({ promptCount, scheduleType, day: currentDay }),
      PREVIEW_DEBOUNCE_MS
    );
    return () => window.clearTimeout(handle);
  }, [open, promptCount, scheduleType, currentDay]);

  const previewRequest = useMemo(() => buildPreviewRequest(debounced), [debounced]);

  /**
   * Keyed on the configuration itself, which is what keeps a slow answer from
   * overwriting a newer one: a response is written to the cache entry of the
   * key it was launched for, so a preview for a configuration the customer has
   * already moved on from can never reappear as the current one.
   */
  const previewQuery = useQuery({
    // Built by the shared helper, so this key and the re-check in `handleSubmit`
    // cannot drift apart. The tenant is part of it, not just of the request:
    // without it a cached price would survive a switch to another tenant's
    // project and be shown against that tenant's bars.
    queryKey: bookingPreviewKey(projectId, tenantId, previewRequest),
    queryFn: () => {
      // Unreachable - `enabled` below only runs this with a request in hand.
      if (!previewRequest) throw new Error('Keine Konfiguration für die Vorschau');
      return previewBooking(previewRequest, tenantId);
    },
    enabled: open && previewRequest !== null,
    retry: false,
    /**
     * A verdict is never cached. This carried `staleTime: 15_000`, which meant
     * that creating an autopilot and reopening the dialog inside that window
     * hit the same key with data still fresh: no refetch, the old "passt" and
     * an enabled save button, beside bars that had been invalidated and did
     * refetch. Reopening now always re-asks, and `previewPending` keeps the
     * button disabled until the new answer lands, so the stale figure can be
     * on screen but never acted on.
     */
    staleTime: 0,
  });

  const preview = previewQuery.data ?? null;
  /** True while the shown preview does not (yet) describe what is on screen. */
  const previewPending =
    promptCount > 0 &&
    (debounced === null ||
      debounced.promptCount !== promptCount ||
      debounced.scheduleType !== scheduleType ||
      debounced.day !== currentDay ||
      previewQuery.isFetching);
  /**
   * Refusing is the server's decision, and `enforced` is where it says so.
   * This used to be `!preview.fits` alone, which had the dialog blocking saves
   * the API deliberately accepts and records: with ENFORCE_MONTHLY_PROMPT_QUOTA
   * off an over-limit configuration is meant to be written down, not turned
   * away, so that the flip can be planned against real tenants. The numbers and
   * the overage are shown either way - `refusalReasons` states them and adds
   * the line that says saving is still possible - so nothing is hidden by not
   * blocking here.
   */
  const previewBlocks = preview !== null && !preview.fits && preview.enforced;

  const createLLMMutation = useMutation({
    mutationFn: (data: CreateAutopilotRequest) => createAutopilot(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['autopilots', projectId] });
      // The preview goes with the bars: this booking is exactly what makes the
      // last verdict wrong. It is only marked stale, not re-asked - this
      // dialog is closing, so the answer would arrive for nobody, and every
      // create was paying for that round trip.
      invalidateQuotaViews(queryClient, { previewRefetch: 'none' });
      if (data.quota_unavailable) {
        // The autopilot was created - only the quota read for this response
        // failed. Closing silently would leave the panel behind showing
        // pre-booking numbers with nothing saying they are stale, and a
        // customer who retries gets a second autopilot.
        setNotice(
          'Der Autopilot wurde erstellt. Das gebuchte Kontingent konnte gerade nicht ' +
            'gelesen werden - die Anzeige aktualisiert sich, sobald es wieder abrufbar ist.'
        );
        return;
      }
      handleClose();
    },
    onError: (err: unknown) => {
      setError(describeApiError(err, 'Fehler beim Erstellen des Autopilots'));
    },
  });

  const handleClose = () => {
    setError(null);
    setNotice(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);

    if (!project || !project.jobs || project.jobs.length === 0) {
      setError('Projekt hat noch keine Jobs. Erstellen Sie zuerst einen Job, bevor Sie einen Autopilot erstellen.');
      return;
    }

    if (promptSelection.length === 0) {
      setError('Der letzte Job hat keine Prompts. Bitte erstellen Sie einen Job mit Prompts.');
      return;
    }

    const schedule = buildScheduleConfig(scheduleType, currentDay);
    if (!schedule) {
      setError(`Bitte einen Tag zwischen 1 und ${MAX_DAY_OF_MONTH} wählen.`);
      return;
    }

    /**
     * Ask again, right now, instead of sending on the strength of the verdict
     * on screen. Not caching one closes the window that this dialog opens for
     * itself; it cannot close the window somebody else opens, and a second
     * customer booking the last of the month between the preview and the save
     * produces the same rejection the preview exists to prevent. `fetchQuery`
     * writes into the very key the panel reads, so a refusal does not merely
     * stop the save - the bars and the reason beneath them redraw to the
     * numbers that refused it.
     */
    const request = buildPreviewRequest({ promptCount, scheduleType, day: currentDay });
    if (request) {
      setRevalidating(true);
      let verdict: BookingPreview;
      try {
        verdict = await queryClient.fetchQuery({
          queryKey: bookingPreviewKey(projectId, tenantId, request),
          queryFn: () => previewBooking(request, tenantId),
          staleTime: 0,
          retry: false,
        });
      } catch (err) {
        setError(
          describeApiError(
            err,
            'Das Kontingent konnte gerade nicht geprüft werden. Bitte erneut versuchen.'
          )
        );
        return;
      } finally {
        setRevalidating(false);
      }
      // Same contract as the button: the flag alone decides whether a
      // configuration outside the budget is a refusal. With enforcement off the
      // create below is accepted and recorded, which is the whole point of the
      // observation phase.
      if (!verdict.fits && verdict.enforced) {
        setError('Diese Konfiguration passt nicht mehr in das Kontingent - siehe Begründung oben.');
        return;
      }
    }

    // Create LLM Score autopilot
    const llmData: CreateAutopilotRequest = {
      project_id: projectId,
      autopilot_type: 'llm_score',
      llm_config: {
        prompts: promptSelection,
      },
      ...schedule,
    };

    try {
      await createLLMMutation.mutateAsync(llmData);
    } catch (err) {
      // Error already handled by mutation
    }
  };

  const isLoading = createLLMMutation.isPending || projectLoading || revalidating;
  /**
   * `available === 0` deliberately does not appear here. It counts `consumed`,
   * which manual tests and retests spend and which never enters the
   * steady-state check - so a tenant that spent its month on manual runs can sit
   * at `available === 0` while a monthly autopilot whose day has already passed
   * costs nothing this month and clears both of the server's checks. That is the
   * case the monthly cadence exists for. `previewBlocks` answers this
   * authoritatively - `fits` for the arithmetic, `enforced` for whether the
   * server turns that arithmetic into a refusal - and the server answers it
   * again on save.
   *
   * The gate is `preview !== null && !previewBlocks`, not `!previewBlocks`
   * alone. Written the second way an absent verdict passes it, so a preview
   * that timed out or 500ed left the button enabled and the customer saved
   * with neither of the two checks ever shown - the surprise rejection this
   * dialog exists to prevent, arriving through the dialog itself. An unknown
   * verdict is therefore not a passing one: the save waits, and the failure
   * line below offers the retry that actually resolves it. Blocking is the
   * right side to fail on because a *known* verdict may still be a refusal,
   * and all a blind save would buy is the same "no", later and without a
   * reason attached.
   *
   * `isError` is its own term and not covered by `preview`: React Query keeps
   * the last successful `data` when a refetch fails, so a failed re-check
   * leaves a `fits: true` on the screen underneath the sentence that says
   * saving is blocked. No unsafe save could result - the submit path re-checks
   * and refuses - but the button would contradict the sentence, which is worse
   * than either alone.
   */
  const canSubmit =
    !isLoading &&
    notice === null &&
    promptCount > 0 &&
    (scheduleType !== 'monthly' || dayValid) &&
    preview !== null &&
    !previewBlocks &&
    !previewQuery.isError &&
    !previewPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Autopilot für {projectName} erstellen</DialogTitle>
      <DialogContent>
        {projectLoading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}
        {!projectLoading && (
          <>
            {/*
              A warning, not an error, and only once the quota is actually
              known: an exhausted month does not by itself rule out a
              configuration that costs nothing until the next one. The preview
              below decides, and the server decides again on save.
            */}
            {quota && quota.available === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Das Kontingent für diesen Monat ist ausgeschöpft. Eine Konfiguration, die in
                diesem Monat nichts mehr kostet, lässt sich trotzdem anlegen - die Vorschau
                unten sagt, ob sie passt.
              </Alert>
            )}
            {notice && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {notice}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  LLM Score Test
                </Typography>
                <Typography variant="body2">
                  Führt in der gewählten Frequenz um 7:00 Uhr einen LLM Score Test mit den
                  bestehenden Prompts des Projekts und der zentral festgelegten
                  Modellaufstellung durch.
                </Typography>
              </Alert>

              <Typography variant="subtitle2" gutterBottom id="autopilot-cadence-label">
                Frequenz
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                aria-labelledby="autopilot-cadence-label"
                value={scheduleType}
                orientation={isMobile ? 'vertical' : 'horizontal'}
                onChange={(_event, value: ScheduleType | null) => {
                  if (value === null) return;
                  if (value === 'monthly' && !dayValid) {
                    setDayInput(String(defaultDayOfMonth()));
                  }
                  setScheduleType(value);
                }}
                sx={{ mb: 2 }}
              >
                {CADENCES.map((cadence) => (
                  <ToggleButton
                    key={cadence.value}
                    value={cadence.value}
                    sx={{ textTransform: 'none', flexDirection: 'column', py: 1 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {cadence.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {cadence.runs}
                    </Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {scheduleType === 'monthly' && (
                <TextField
                  label="Tag im Monat"
                  type="number"
                  size="small"
                  value={dayInput}
                  onChange={(event) => setDayInput(event.target.value)}
                  inputProps={{ min: 1, max: MAX_DAY_OF_MONTH, step: 1 }}
                  error={!dayValid}
                  helperText={
                    dayValid
                      ? 'Tage nach dem 28. sind nicht wählbar, damit der Lauf auch im Februar stattfindet.'
                      : `Bitte einen Tag zwischen 1 und ${MAX_DAY_OF_MONTH} wählen.`
                  }
                  sx={{ mb: 2, maxWidth: 320 }}
                />
              )}

              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {promptCount > 0
                  ? `${promptCount} Prompt${promptCount > 1 ? 's' : ''} aus dem letzten Job dieses Projekts.`
                  : 'Für dieses Projekt gibt es noch keinen Job mit Prompts.'}
              </Typography>

              {preview && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Diese Konfiguration kostet{' '}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {preview.initial_booking} Prompts
                  </Box>{' '}
                  im laufenden Monat (verbleibende Läufe: {preview.remaining_runs}) und{' '}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {preview.full_month_cost} Prompts
                  </Box>{' '}
                  ab dem Folgemonat.
                </Typography>
              )}

              {/*
                Three states, not two. `quota === undefined` used to mean both
                "still loading" and "gave up", and a failed read then sat under
                "wird geladen …" for as long as the dialog stayed open. The
                bars are context, not the verdict - the preview is - so a
                failed quota read explains itself and leaves the preview to
                decide whether saving is allowed.

                Without the panel the refusal has to be stated here. It used to
                live inside the panel, so a failed quota read produced the one
                state worse than no explanation: "Passt nicht in das Kontingent
                - siehe Begründung oben" with no Begründung anywhere, beside a
                line promising the preview still applies. Both no-panel
                branches render it, loading included - a preview can refuse
                before the quota has arrived just as easily as after it failed.
              */}
              {quota ? (
                <MonthlyQuotaPanel quota={quota} preview={preview} />
              ) : (
                <Stack spacing={2}>
                  {quotaError ? (
                    <Typography variant="body2" sx={{ color: QUOTA_OVER_LIMIT_INK }}>
                      Das Kontingent konnte nicht geladen werden - die Balken fehlen deshalb. Die
                      Kostenvorschau oben gilt trotzdem.
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Kontingent wird geladen …
                    </Typography>
                  )}
                  <QuotaRefusal reasons={refusalReasons(preview)} />
                </Stack>
              )}

              {/*
                Always mounted, like the region in the actions below: a live
                region created in the same tick as its text is not reliably
                announced, and this one reports the loss of the only thing that
                answers "will this be accepted".
              */}
              <Box role="status" aria-live="polite" sx={{ mt: 1 }}>
                {previewQuery.isError && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: QUOTA_OVER_LIMIT_INK }}>
                      Die Kostenvorschau ist gerade nicht verfügbar. Ohne sie lässt sich nicht
                      sagen, ob die Konfiguration in das Kontingent passt - Speichern ist so lange
                      gesperrt.
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => previewQuery.refetch()}
                      disabled={previewQuery.isFetching}
                    >
                      Erneut prüfen
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {/*
          Always mounted, so the region exists before it has anything to say - a
          live region created at the same moment as its text is not reliably
          announced. It matters here because the control this explains is
          `disabled` and therefore cannot be reached to ask why.
        */}
        <Box role="status" aria-live="polite" sx={{ mr: 'auto', pl: 1 }}>
          {previewBlocks && (
            <Typography variant="caption" sx={{ color: QUOTA_OVER_LIMIT_INK, fontWeight: 600 }}>
              Passt nicht in das Kontingent - siehe Begründung oben.
            </Typography>
          )}
        </Box>
        {notice ? (
          <Button onClick={handleClose} variant="contained">
            Schließen
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button onClick={handleClose} disabled={isLoading}>
              Abbrechen
            </Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
              {isLoading ? <CircularProgress size={24} /> : 'Erstellen'}
            </Button>
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateAutopilotDialog;
